/**
 * VELARE Waitlist Form Handler
 * Shopify-compatible waitlist signup via the Contact Form / Customer API.
 * No dependencies. Loaded via <script> tag.
 *
 * Expected HTML structure:
 *
 *   <form data-waitlist-form
 *         action="/contact#waitlist-form"
 *         method="POST">
 *     <input type="hidden" name="form_type" value="customer">
 *     <input type="hidden" name="utf8" value="&#x2713;">
 *     <input type="hidden" name="contact[tags]" value="waitlist,newsletter">
 *     <input type="email"
 *            name="contact[email]"
 *            data-waitlist-email
 *            required
 *            placeholder="Your email"
 *            aria-label="Email address">
 *     <button type="submit" data-waitlist-button>
 *       <span data-waitlist-button-text>Join</span>
 *     </button>
 *     <div data-waitlist-message aria-live="polite"></div>
 *   </form>
 *
 * States: idle | loading | success | error
 */
;(function () {
  'use strict'

  /* ------------------------------------------------------------------ */
  /*  Constants                                                         */
  /* ------------------------------------------------------------------ */

  var BUTTON_TEXT = {
    idle: 'Join',
    loading: 'Joining\u2026',
    success: 'Joined',
    error: 'Join',
  }

  var SUCCESS_MESSAGE = 'Welcome to the inner circle.'
  var GENERIC_ERROR = 'Something went wrong. Please try again.'
  var NETWORK_ERROR = 'Network error. Please try again.'
  var INVALID_EMAIL = 'Please enter a valid email address.'
  var DUPLICATE_MESSAGE = 'This email is already on the waitlist.'

  // Simple email regex — intentionally permissive; the server validates too.
  var EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

  /* ------------------------------------------------------------------ */
  /*  Helpers                                                           */
  /* ------------------------------------------------------------------ */

  /**
   * Serialize form data into URL-encoded string (for fetch body).
   */
  function serializeForm(form) {
    var pairs = []
    var elements = form.elements

    for (var i = 0; i < elements.length; i++) {
      var el = elements[i]
      if (!el.name || el.disabled) continue
      if (el.type === 'checkbox' && !el.checked) continue
      if (el.type === 'radio' && !el.checked) continue
      pairs.push(
        encodeURIComponent(el.name) + '=' + encodeURIComponent(el.value)
      )
    }

    return pairs.join('&')
  }

  /* ------------------------------------------------------------------ */
  /*  State management                                                  */
  /* ------------------------------------------------------------------ */

  /**
   * Apply a state to the form UI.
   * @param {HTMLFormElement} form
   * @param {'idle'|'loading'|'success'|'error'} state
   * @param {string} [message]
   */
  function setState(form, state, message) {
    var emailInput = form.querySelector('[data-waitlist-email]') || form.querySelector('input[type="email"]')
    var button = form.querySelector('[data-waitlist-button]') || form.querySelector('button[type="submit"]')
    var buttonText = form.querySelector('[data-waitlist-button-text]')
    var messageEl = form.querySelector('[data-waitlist-message]')

    // Update button text.
    if (buttonText) {
      buttonText.textContent = BUTTON_TEXT[state] || BUTTON_TEXT.idle
    } else if (button) {
      // Fallback: set text directly on button if no span wrapper.
      // Preserve any chevron icon by only changing the first text node.
      var textNode = null
      for (var i = 0; i < button.childNodes.length; i++) {
        if (button.childNodes[i].nodeType === Node.TEXT_NODE && button.childNodes[i].textContent.trim()) {
          textNode = button.childNodes[i]
          break
        }
      }
      if (textNode) {
        textNode.textContent = BUTTON_TEXT[state] || BUTTON_TEXT.idle
      }
    }

    // Disable / enable inputs.
    var isDisabled = state === 'loading' || state === 'success'
    if (emailInput) emailInput.disabled = isDisabled
    if (button) button.disabled = isDisabled

    // Message.
    if (messageEl) {
      messageEl.textContent = message || ''
      // Remove previous state classes.
      messageEl.classList.remove('is-success', 'is-error')
      if (state === 'success') {
        messageEl.classList.add('is-success')
      } else if (state === 'error') {
        messageEl.classList.add('is-error')
      }
    }

    // Store state on the form for CSS hooks.
    form.setAttribute('data-state', state)
  }

  /* ------------------------------------------------------------------ */
  /*  Submit handler                                                    */
  /* ------------------------------------------------------------------ */

  function handleSubmit(e) {
    e.preventDefault()

    var form = e.target
    var emailInput = form.querySelector('[data-waitlist-email]') || form.querySelector('input[type="email"]')
    if (!emailInput) return

    var email = (emailInput.value || '').trim()

    // Client-side validation.
    if (!email) {
      setState(form, 'error', INVALID_EMAIL)
      emailInput.focus()
      return
    }

    if (!EMAIL_REGEX.test(email)) {
      setState(form, 'error', INVALID_EMAIL)
      emailInput.focus()
      return
    }

    // Enter loading state.
    setState(form, 'loading')

    // Build the POST body from the form fields.
    var body = serializeForm(form)

    // Shopify's contact form endpoint.
    var action = form.getAttribute('action') || '/contact'

    fetch(action, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        Accept: 'application/json, text/html',
      },
      body: body,
    })
      .then(function (response) {
        // Shopify may return a 200 redirect page even on success.
        // A 302/303 redirect means success; fetch follows it automatically.
        // If the response is a full HTML page, we parse for error indicators.

        if (response.ok || response.redirected) {
          return response.text().then(function (html) {
            // Check for Shopify's error patterns in the returned HTML.
            var lowerHtml = html.toLowerCase()

            // Shopify returns specific error messages in the HTML.
            if (lowerHtml.indexOf('error') > -1 && lowerHtml.indexOf('already') > -1) {
              // Email already exists — treat as soft success for waitlist.
              setState(form, 'success', DUPLICATE_MESSAGE)
              emailInput.value = ''
              return
            }

            // Check for explicit form errors from Shopify.
            if (lowerHtml.indexOf('class="errors"') > -1 || lowerHtml.indexOf('data-form-errors') > -1) {
              // Try to extract the error message.
              var errorMatch = html.match(/<li[^>]*>\s*([^<]+)\s*<\/li>/)
              var errorMsg = errorMatch ? errorMatch[1].trim() : GENERIC_ERROR
              setState(form, 'error', errorMsg)
              return
            }

            // No errors detected — assume success.
            setState(form, 'success', SUCCESS_MESSAGE)
            emailInput.value = ''
          })
        }

        // Non-OK, non-redirect response.
        if (response.status === 422) {
          // Shopify returns 422 for validation errors (e.g. duplicate).
          return response.text().then(function (html) {
            if (html.toLowerCase().indexOf('already') > -1 ||
                html.toLowerCase().indexOf('taken') > -1 ||
                html.toLowerCase().indexOf('exists') > -1) {
              setState(form, 'success', DUPLICATE_MESSAGE)
              emailInput.value = ''
            } else {
              setState(form, 'error', GENERIC_ERROR)
            }
          })
        }

        setState(form, 'error', GENERIC_ERROR)
      })
      .catch(function () {
        setState(form, 'error', NETWORK_ERROR)
      })
  }

  /* ------------------------------------------------------------------ */
  /*  Init                                                              */
  /* ------------------------------------------------------------------ */

  function init() {
    var forms = document.querySelectorAll('[data-waitlist-form]')
    if (!forms.length) return

    forms.forEach(function (form) {
      // Set initial state.
      setState(form, 'idle')

      // Attach submit handler.
      form.addEventListener('submit', handleSubmit)

      // Clear error state when user starts typing again.
      var emailInput = form.querySelector('[data-waitlist-email]') || form.querySelector('input[type="email"]')
      if (emailInput) {
        emailInput.addEventListener('input', function () {
          if (form.getAttribute('data-state') === 'error') {
            setState(form, 'idle')
          }
        })
      }
    })
  }

  // Boot when DOM is ready.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init)
  } else {
    init()
  }
})()
